import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, switchMap, throwError } from 'rxjs';
import { environment } from '@dxc/environments';
import { DXCAuthService } from './dxc-auth.service';

// Defining a context token to optionally bypass this interceptor
export const BYPASS_AUTH_TOKEN = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  // Skip auth for bypass-flagged requests, static translation files, and
  // anything that isn't our own API — never attach the bearer token elsewhere.
  if (
    request.context.get(BYPASS_AUTH_TOKEN) ||
    request.url.startsWith('/i18n/') ||
    !request.url.startsWith(environment.apiUrl)
  ) {
    return next(request);
  }

  const router = inject(Router);
  const dxcAuthService = inject(DXCAuthService);
  const authToken = dxcAuthService.getAuthToken();
  const isLoggedIn = dxcAuthService.isLoggedIn();

  if (!authToken || !isLoggedIn) {
    if (!router.url.startsWith('/login')) {
      // Token is expired — try a silent refresh if the user was recently active
      if (dxcAuthService.isRecentlyActive() && dxcAuthService.getRefreshToken()) {
        return dxcAuthService.refreshAccessToken().pipe(
          switchMap((newToken: string) => next(attachToken(request, newToken))),
          catchError(() => {
            dxcAuthService.clearSessionAndNavigateToAuth(router);
            return EMPTY;
          })
        );
      }
      dxcAuthService.clearSessionAndNavigateToAuth(router);
    }
    return EMPTY;
  }

  return next(attachToken(request, authToken)).pipe(
    catchError(handleHttpError(dxcAuthService, router, request, next))
  );
};

function attachToken(request: HttpRequest<unknown>, token: string) {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`)
  });
}

function handleHttpError(
  authService: DXCAuthService,
  router: Router,
  originalRequest: HttpRequest<unknown>,
  next: HttpHandlerFn
) {
  return (error: HttpErrorResponse) => {
    if (error.status === 401 && !router.url.startsWith('/login')) {
      // Try a silent refresh before giving up
      if (authService.isRecentlyActive() && authService.getRefreshToken()) {
        return authService.refreshAccessToken().pipe(
          switchMap((newToken: string) => next(attachToken(originalRequest, newToken))),
          catchError(() => {
            authService.clearSessionAndNavigateToAuth(router);
            return EMPTY;
          })
        );
      }
      authService.clearSessionAndNavigateToAuth(router);
      return EMPTY;
    }
    return throwError(() => error);
  };
}
